import type { CSSInterpolation } from '@emotion/serialize';

import { BuildType } from 'src/enums/build/build-type';
import { borderRadius } from 'src/styling/constants';

import type { Colors } from './colors';
import type { Fonts } from './fonts';

export type Buttons = {
  primary: CSSInterpolation;
  disabled: CSSInterpolation;
  highlight: CSSInterpolation;
  secondary: CSSInterpolation;
  secondaryNoHover: CSSInterpolation;
  secondaryDisabled: CSSInterpolation;
  transparent: CSSInterpolation;
  transparentWithWhiteFont?: CSSInterpolation;
  secondaryTransparent: CSSInterpolation;
  alert: CSSInterpolation;
  darkGreen?: CSSInterpolation;
  plum?: CSSInterpolation;
  whiteWithPlumFont?: CSSInterpolation;
  whiteWithGreenFont?: CSSInterpolation;
  orange?: CSSInterpolation;
  blue?: CSSInterpolation;
  transparentWithWhiteFontAndBorder?: CSSInterpolation;
  transparentWithBlueFontAndBorder?: CSSInterpolation;
  withoutBordersWithWhiteFont?: CSSInterpolation;
  withoutBordersWithBlueFont?: CSSInterpolation;
  harbourGreyWithWhiteFont?: CSSInterpolation;
  transparentWithGreyFontAndBorder?: CSSInterpolation;
  transparentWithGoldFontAndBorder?: CSSInterpolation;
  whiteWithHarbourGreyFont?: CSSInterpolation;
};

export const getButtons = (buildType: BuildType, colors: Colors, fonts: Fonts) => {
  const isMeridian = buildType === BuildType.MERIDIAN;
  const button: CSSInterpolation = {
    border: 'none',
    borderRadius: isMeridian ? borderRadius.meridianButton : borderRadius.button,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    transition: 'background-color 0.2s ease',
    cursor: 'pointer',
    padding: '1rem',
    maxWidth: '20rem',
    fontSize: '1rem',
    lineHeight: '1',
  };

  return {
    primary: [
      button,
      fonts.bold,
      {
        'color': 'white',
        'backgroundColor': colors.highlightText,
        'border': `3px solid transparent`,
        '&:hover': {
          backgroundColor: colors.darkHighlight,
        },
      },
    ],
    disabled: [
      button,
      fonts.bold,
      {
        backgroundColor: colors.fadedHighlight,
        border: `3px solid transparent`,
        color: 'white',
        cursor: 'not-allowed',
      },
    ],
    highlight: [
      button,
      fonts.bold,
      {
        'color': 'white',
        'backgroundColor': colors.goldHighlight,
        'border': `3px solid transparent`,
        '&:hover': {
          backgroundColor: colors.darkGoldHighlight,
        },
      },
    ],
    secondary: [
      button,
      fonts.bold,
      {
        'color': colors.highlightText,
        'backgroundColor': isMeridian ? colors.secondaryHighlight : 'white',
        'border': `3px solid ${isMeridian ? 'transparent' : colors.fadedHighlight}`,
        '&:hover': {
          color: 'white',
          backgroundColor: colors.highlightText,
          border: `3px solid ${colors.darkHighlight}`,
        },
      },
    ],
    secondaryNoHover: [
      button,
      fonts.bold,
      {
        color: colors.highlightText,
        backgroundColor: 'white',
        border: `3px solid ${colors.fadedHighlight}`,
      },
    ],
    secondaryDisabled: [
      button,
      fonts.bold,
      {
        border: `3px solid ${colors.fadedHighlight}`,
        color: colors.fadedHighlight,
        backgroundColor: 'white',
        cursor: 'auto',
      },
    ],
    transparent: [
      button,
      fonts.bold,
      {
        'color': 'white',
        'backgroundColor': 'transparent',
        'border': `3px solid ${colors.highlightText}`,
        '&:hover': {
          color: 'white',
          backgroundColor: colors.highlightText,
          border: `3px solid ${colors.darkHighlight}`,
        },
      },
    ],
    secondaryTransparent: [
      button,
      fonts.bold,
      {
        'color': colors.highlightText,
        'backgroundColor': 'transparent',
        'border': `3px solid ${colors.highlightText}`,
        '&:hover': {
          color: 'white',
          backgroundColor: colors.highlightText,
          border: `3px solid ${colors.darkHighlight}`,
        },
      },
    ],
    alert: [
      button,
      fonts.bold,
      {
        'color': colors.alert,
        'backgroundColor': 'white',
        'border': `3px solid transparent`,
        '&:hover': {
          color: 'white',
          backgroundColor: colors.darkHighlight,
        },
      },
    ],
  } as Buttons;
};

export const getRedesignButtons = (colors: Colors, fonts: Fonts) => {
  const button: CSSInterpolation = {
    borderRadius: borderRadius.redesignButton,
    textAlign: 'center',
    transition: 'background-color 0.2s ease',
    cursor: 'pointer',
    padding: '0.75rem 1.5rem',
    fontSize: '1.1875rem',
    lineHeight: '100%',
    height: '3rem',
    display: 'flex',
    alignItems: 'center',
    maxWidth: 'fit-content',
    justifyContent: 'center',
  };

  const transparentButtonStyle: CSSInterpolation = [
    button,
    fonts.regular,
    {
      'color': colors.camelot,
      'backgroundColor': 'transparent',
      'border': `1px solid ${colors.camelot}`,
      '&:hover': {
        backgroundColor: colors.camelot,
        color: 'white',
      },
      '&:focus': {
        border: `1px solid ${colors.camelot}`,
        color: colors.camelot,
        backgroundColor: 'transparent',
      },
      '& svg': {
        fill: `${colors.camelot} !important`,
        verticalAlign: 'middle',
        marginLeft: '0.75rem',
      },
      '&:hover svg': {
        fill: 'white !important',
      },
      '&:focus svg': {
        fill: `${colors.camelot} !important`,
      },
    },
  ];

  const plumButtonStyle: CSSInterpolation = [
    button,
    fonts.regular,
    {
      'color': 'white',
      'backgroundColor': colors.camelot,
      'border': `1px solid transparent`,
      '&:hover': {
        backgroundColor: colors.vibrant,
      },
      '&:focus': {
        backgroundColor: colors.camelot,
        border: `1px solid white`,
      },
      '& svg': {
        fill: 'white !important',
        verticalAlign: 'middle',
        marginLeft: '0.75rem',
      },
    },
  ];

  return {
    plum: plumButtonStyle,
    darkGreen: [
      button,
      fonts.regular,
      {
        'color': 'white',
        'backgroundColor': colors.darkGreen,
        'border': `1px solid transparent`,
        '&:hover': {
          backgroundColor: 'rgba(35, 73, 75, 0.80)',
        },
        '&:focus': {
          backgroundColor: colors.darkGreen,
          border: `1px solid white`,
        },
        '& svg': {
          fill: 'white !important',
          verticalAlign: 'middle',
          marginLeft: '0.75rem',
        },
      },
    ],
    transparent: transparentButtonStyle,
    transparentWithWhiteFont: [
      ...transparentButtonStyle,
      {
        'color': 'white',
        'border': `1px solid white`,
        '& svg': {
          fill: `white !important`,
          verticalAlign: 'middle',
          marginLeft: '0.75rem',
        },
        '&:focus': {
          border: `1px solid white`,
          color: 'white',
          backgroundColor: 'transparent',
        },
        '&:focus svg': {
          fill: `white !important`,
        },
      },
    ],
    whiteWithPlumFont: [
      ...transparentButtonStyle,
      {
        'backgroundColor': colors.lightBackground,
        'border': 'none',
        '&:hover': {
          color: 'white',
          backgroundColor: colors.camelot,
        },
        '&:focus': {
          color: colors.camelot,
          backgroundColor: colors.lightBackground,
          border: 'none',
          boxShadow: `unset`,
        },
        '&:focus svg': {
          fill: `${colors.camelot} !important`,
        },
      },
    ],
    whiteWithGreenFont: [
      ...transparentButtonStyle,
      {
        'backgroundColor': colors.lightBackground,
        'color': colors.darkGreen,
        'border': 'none',
        '&:hover': {
          color: 'white',
          backgroundColor: colors.camelot,
        },
        '& svg': {
          fill: `${colors.darkGreen} !important`,
        },
        '&:focus': {
          color: colors.darkGreen,
          backgroundColor: colors.lightBackground,
          border: 'none',
          boxShadow: `unset`,
        },
        '&:focus svg': {
          fill: `${colors.darkGreen} !important`,
        },
      },
    ],
    disabled: [
      button,
      fonts.bold,
      {
        backgroundColor: colors.fadedHighlight,
        border: `1px solid transparent`,
        color: 'white',
        cursor: 'not-allowed',
      },
    ],
    highlight: [
      button,
      fonts.bold,
      {
        'color': 'white',
        'backgroundColor': colors.goldHighlight,
        'border': `1px solid transparent`,
        '&:hover': {
          backgroundColor: colors.darkGoldHighlight,
        },
      },
    ],
    secondaryNoHover: [
      button,
      fonts.bold,
      {
        color: colors.highlightText,
        backgroundColor: 'white',
        border: `1px solid ${colors.fadedHighlight}`,
      },
    ],
    secondaryDisabled: [
      button,
      fonts.bold,
      {
        border: `1px solid ${colors.fadedHighlight}`,
        color: colors.fadedHighlight,
        backgroundColor: 'white',
        cursor: 'auto',
      },
    ],
    secondaryTransparent: [
      button,
      fonts.bold,
      {
        'color': colors.highlightText,
        'backgroundColor': 'transparent',
        'border': `1px solid ${colors.highlightText}`,
        '&:hover': {
          color: 'white',
          backgroundColor: colors.highlightText,
          border: `1px solid ${colors.darkHighlight}`,
        },
      },
    ],
    alert: [
      button,
      fonts.bold,
      {
        'color': colors.alert,
        'backgroundColor': 'white',
        'border': `1px solid transparent`,
        '&:hover': {
          color: 'white',
          backgroundColor: colors.darkHighlight,
        },
      },
    ],
    primary: plumButtonStyle,
    secondary: transparentButtonStyle,
    orange: [
      button,
      fonts.regular,
      {
        'color': 'white',
        'backgroundColor': colors.meridianOrange,
        'border': `1px solid transparent`,
        'fontWeight': 'bold',
        'borderRadius': borderRadius.redesignMeridianButton,
        '&:hover': {
          backgroundColor: colors.meridianOrangeHover,
        },
        '&:focus': {
          border: `1px solid ${colors.meridianOrange}`,
        },
        '& svg': {
          fill: 'white !important',
          verticalAlign: 'middle',
          marginLeft: '0.75rem',
        },
      },
    ],
    blue: [
      button,
      fonts.regular,
      {
        'color': 'white',
        'backgroundColor': colors.meridianBlue,
        'border': `1px solid transparent`,
        'fontWeight': 'normal',
        'borderRadius': borderRadius.redesignMeridianButton,
        '&:hover': {
          backgroundColor: colors.meridianFadeBlue,
        },
        '&:focus': {
          border: `1px solid ${colors.meridianBlue}`,
        },
        '& svg': {
          fill: 'white !important',
          verticalAlign: 'middle',
          marginLeft: '0.75rem',
        },
      },
    ],
    transparentWithBlueFontAndBorder: [
      button,
      fonts.regular,
      {
        'color': colors.meridianBlue,
        'backgroundColor': 'transparent',
        'border': `1px solid ${colors.meridianBlue}`,
        'borderRadius': borderRadius.redesignMeridianButton,
        '&:hover': {
          'color': colors.meridianFadeBlue,
          'border': `1px solid ${colors.meridianFadeBlue}`,
          '& svg': {
            fill: `${colors.meridianFadeBlue} !important`,
          },
        },
        '&:focus': {
          border: `1px solid ${colors.meridianBlue}`,
        },
        '& svg': {
          fill: `${colors.meridianBlue} !important`,
          verticalAlign: 'middle',
          marginLeft: '0.75rem',
        },
      },
    ],
    transparentWithWhiteFontAndBorder: [
      button,
      fonts.regular,
      {
        'color': 'white',
        'backgroundColor': 'transparent',
        'border': `1px solid white`,
        'borderRadius': borderRadius.redesignMeridianButton,
        '&:hover': {
          'color': colors.meridianBrightBlue,
          'border': `1px solid ${colors.meridianBrightBlue}`,
          '& svg': {
            fill: `${colors.meridianBrightBlue} !important`,
          },
        },
        '&:focus': {
          border: `1px solid white`,
        },
        '& svg': {
          fill: 'white !important',
          verticalAlign: 'middle',
          marginLeft: '0.75rem',
        },
      },
    ],
    withoutBordersWithWhiteFont: [
      button,
      fonts.regular,
      {
        'color': 'white',
        'backgroundColor': 'transparent',
        'border': `1px solid transparent`,
        'borderRadius': borderRadius.redesignMeridianButton,
        'padding': '0.75rem 0',
        '&:hover': {
          'color': colors.meridianBrightBlue,
          '& svg': {
            fill: `${colors.meridianBrightBlue} !important`,
          },
        },
        '&:focus': {
          fontWeight: 'bold',
        },
        '& svg': {
          fill: `white !important`,
          verticalAlign: 'middle',
          marginLeft: '0.75rem',
        },
      },
    ],
    withoutBordersWithBlueFont: [
      button,
      fonts.regular,
      {
        'color': colors.meridianBlue,
        'backgroundColor': 'transparent',
        'border': `1px solid transparent`,
        'borderRadius': borderRadius.redesignMeridianButton,
        'padding': '0.75rem 0',
        '&:hover': {
          'color': colors.meridianFadeBlue,
          '& svg': {
            fill: `${colors.meridianFadeBlue} !important`,
          },
        },
        '&:focus': {
          fontWeight: 'bold',
        },
        '& svg': {
          fill: `${colors.meridianBlue} !important`,
          verticalAlign: 'middle',
          marginLeft: '0.75rem',
        },
      },
    ],
    harbourGreyWithWhiteFont: [
      ...plumButtonStyle,
      {
        'padding': '0.75rem 1.5rem',
        'lineHeight': '1.1',
        'backgroundColor': colors.harbourSlateGrey,
        '&:hover': {
          backgroundColor: colors.harbourHoverGrey,
        },
        '&:focus': {
          backgroundColor: colors.harbourSlateGrey,
        },
      },
    ],
    transparentWithGreyFontAndBorder: [
      ...transparentButtonStyle,
      {
        'padding': '0.75rem 1.5rem',
        'lineHeight': '1.1',
        'color': colors.harbourSlateGrey,
        'border': `1px solid ${colors.harbourSlateGrey}`,
        '&:hover': {
          backgroundColor: colors.harbourSlateGrey,
          color: 'white',
        },
        '& svg': {
          fill: `${colors.harbourSlateGrey} !important`,
          verticalAlign: 'middle',
          marginLeft: '0.75rem',
        },
        '&:hover svg': {
          fill: 'white !important',
        },
        '&:focus': {
          border: `1px solid ${colors.harbourSlateGrey}`,
          color: colors.harbourSlateGrey,
          backgroundColor: 'transparent',
        },
        '&:focus svg': {
          fill: `${colors.harbourSlateGrey} !important`,
        },
      },
    ],
    transparentWithGoldFontAndBorder: [
      ...transparentButtonStyle,
      {
        'padding': '0.75rem 1.5rem',
        'lineHeight': '1.1',
        'color': colors.harbourGold,
        'backgroundColor': 'transparent',
        'border': `1px solid ${colors.harbourGold}`,
        '&:hover': {
          color: 'white',
          backgroundColor: colors.harbourGold,
        },
        '& svg': {
          fill: `${colors.harbourGold} !important`,
          verticalAlign: 'middle',
          marginLeft: '0.75rem',
        },
        '&:hover svg': {
          fill: 'white !important',
        },
        '&:focus': {
          color: colors.harbourGold,
          backgroundColor: 'transparent',
          border: `1px solid ${colors.harbourGold}`,
        },
        '&:focus svg': {
          fill: `${colors.harbourGold} !important`,
        },
      },
    ],
    whiteWithHarbourGreyFont: [
      ...transparentButtonStyle,
      {
        'border': `1px solid ${colors.harbourSlateGrey}`,
        'padding': '0.75rem 1.5rem',
        'lineHeight': '1.1',
        'color': colors.harbourSlateGrey,
        'backgroundColor': 'white',
        '&:hover': {
          color: 'white',
          backgroundColor: colors.harbourSlateGrey,
        },
        '& svg': {
          fill: `${colors.harbourSlateGrey} !important`,
          verticalAlign: 'middle',
          marginLeft: '0.75rem',
        },
        '&:hover svg': {
          fill: 'white !important',
        },
        '&:focus': {
          color: colors.harbourSlateGrey,
          backgroundColor: 'white',
          border: 'none',
          boxShadow: `unset`,
        },
        '&:focus svg': {
          fill: `${colors.harbourSlateGrey} !important`,
        },
      },
    ],
  } as Partial<Buttons>;
};
